import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL, BASE_USER_URL } from '../store/apiConfig';
import { showBlockingOverlay } from '../components/Common/UILock';
import { withBase } from '../utils/route.util';

// Set default base URL
axios.defaults.baseURL = BASE_URL;

// Set default headers
axios.defaults.headers.post['Content-Type'] = 'application/json';

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
let failedQueue = [];

const getCompanyCode = () =>
  sessionStorage.getItem('companyCode') ||
  localStorage.getItem('companyCode') ||
  null;

const setCompanyCode = code => {
  const rememberMe = localStorage.getItem('rememberMe') === 'true';
  if (rememberMe) {
    localStorage.setItem('companyCode', code);
  } else {
    sessionStorage.setItem('companyCode', code);
  }
};

// Function to process the failed requests queue
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Request interceptor to add Authorization header
axios.interceptors.request.use(
  config => {
    config.headers.deviceType = 'web';
    const token =
      sessionStorage.getItem('accessToken') ||
      localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.Companycode = getCompanyCode();
    }
    return config;
  },
  error => Promise.reject(error),
);

// Response interceptor to handle errors
axios.interceptors.response.use(
  async response => response.data || response,
  async error => {
    const { response } = error;
    const originalRequest = error.config;

    // Bypass refresh logic for login and verify-otp endpoints
    const bypassRefreshEndpoints = ['/login', '/verify-otp'];
    // You may need to adjust this check depending on your API URL structure
    const isBypassEndpoint = bypassRefreshEndpoints.some(endpoint =>
      originalRequest.url?.includes(endpoint),
    );

    // If the error is due to an invalid token (401 Unauthorized)
    if (
      response?.status === 401 &&
      !originalRequest.retry &&
      !isBypassEndpoint
    ) {
      originalRequest.retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Get remember me status
          const rememberMe = localStorage.getItem('rememberMe') === 'true';

          // Call the refresh token API
          const refreshToken =
            sessionStorage.getItem('refreshToken') ||
            localStorage.getItem('refreshToken');
          const refreshResponse = await axios.post(
            `${BASE_URL}${BASE_USER_URL}/refresh-token`,
            {
              refreshToken,
            },
          );
          const newAccessToken = refreshResponse.data.accessToken;
          const newRefreshToken = refreshResponse.data.refreshToken;

          // Update the token in localStorage and Axios headers
          if (rememberMe) {
            localStorage.setItem('accessToken', newAccessToken);
            localStorage.setItem('refreshToken', newRefreshToken);
          } else {
            sessionStorage.setItem('accessToken', newAccessToken);
            sessionStorage.setItem('refreshToken', newRefreshToken);
          }
          axios.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          axios.defaults.headers.common.Companycode = getCompanyCode();

          // Process the queued requests
          processQueue(null, newAccessToken);

          isRefreshing = false;

          // Retry the original request with the new token
          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          processQueue(refreshError, null);

          // Log out the user if the refresh token fails
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('refreshToken');
          window.location.href = withBase('/login');
          return Promise.reject(refreshError);
        }
      }

      // Queue the failed request until the token is refreshed
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        originalRequest.headers.Companycode = getCompanyCode();
        return axios(originalRequest);
      });
    }

    const suppressToast = Boolean(originalRequest.suppressToast);

    // Handle other errors
    if (response?.data) {
      if (response.status !== 401 && !suppressToast) {
        toast.error(
          response.data.message || typeof response.data === 'string'
            ? response.data
            : 'An error occurred',
          {
            className: 'toast-error',
          },
        );
      }
      return Promise.reject(response.data);
    }
    const message =
      {
        500: 'Internal Server Error',
        401: 'Invalid credentials',
        404: 'Sorry! The data you are looking for could not be found',
      }[response?.status] ||
      error.message ||
      'An unknown error occurred';

    return Promise.reject(message);
  },
);

class APIClient {
  /**
   * Fetches data from the given URL with optional query parameters
   * @param {string} url - The endpoint URL
   * @param {object} [params] - Query parameters
   * @returns {Promise} Axios response
   */
  async get(url, params) {
    let queryString;
    if (params) {
      queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
    }

    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return axios.get(fullUrl);
  }

  /**
   * Fetches data with custom config
   * @param {string} url - The endpoint URL
   * @param {object} config - Axios config
   * @returns {Promise} Axios response
   */
  getWithConfig(url, config) {
    return axios.get(url, config);
  }

  /**
   * Sends a POST request to the given URL with data
   * @param {string} url - The endpoint URL
   * @param {object} data - The request payload
   * @param {object} [config={}] - Axios config
   * @returns {Promise} Axios response
   */
  create(url, data, config = {}) {
    return axios.post(url, data, config);
  }

  /**
   * Sends a PATCH request to update data
   * @param {string} url - The endpoint URL
   * @param {object} data - The request payload
   * @returns {Promise} Axios response
   */
  update(url, data) {
    return axios.patch(url, data);
  }

  /**
   * Sends a PUT request to update data
   * @param {string} url - The endpoint URL
   * @param {object} data - The request payload
   * @param {object} config - Axios config
   * @returns {Promise} Axios response
   */
  put(url, data, config) {
    return axios.put(url, data, config);
  }

  /**
   * Sends a DELETE request to the given URL
   * @param {string} url - The endpoint URL
   * @param {object} config - Axios config
   * @returns {Promise} Axios response
   */
  delete(url, config) {
    return axios.delete(url, { ...config });
  }
}

/**
 * Retrieves the access token from localStorage
 * @returns {string|null} Access token or null if not found
 */
const getToken = () =>
  sessionStorage.getItem('accessToken') ||
  localStorage.getItem('accessToken') ||
  null;

const getRefreshToken = () =>
  sessionStorage.getItem('refreshToken') ||
  localStorage.getItem('refreshToken') ||
  null;

const logoutUser = () => {
  showBlockingOverlay('Signing you out…');

  // Clear client auth immediately
  try {
    localStorage.removeItem('authUser');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('impersonateUsers');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('authUser');
    sessionStorage.removeItem('impersonateUsers');
  } catch {
    // ignore—best effort
  }

  try {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 5000); // timeout
    fetch('/envision/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
    }).catch(() => {});
  } catch {
    // ignore—best effort
  }

  window.location.replace(withBase('/login'));
};

// Create a singleton instance of APIClient
const apiClient = new APIClient();

export {
  apiClient,
  getRefreshToken,
  getToken,
  logoutUser,
  getCompanyCode,
  setCompanyCode,
};
